import apiClient from './apiClient'

/** Primer mensaje útil de una respuesta DRF (detail, error, o primer campo). */
function drfErrorMessage(data, fallback) {
  if (!data || typeof data !== 'object') return fallback
  if (typeof data.detail === 'string') return data.detail
  if (data.error) return String(data.error)
  for (const key of Object.keys(data)) {
    const v = data[key]
    if (Array.isArray(v) && v.length) return String(v[0])
    if (typeof v === 'string') return v
  }
  return fallback
}

export const uploadFile = async (file, options = {}) => {
  const formData = new FormData()
  formData.append('file', file)
  if (options.subdirectory) {
    formData.append('subdirectory', options.subdirectory)
  }

  try {
    const response = await apiClient.post('/uploads/', formData)
    return response.data
  } catch (error) {
    throw new Error(
      drfErrorMessage(error.response?.data, 'Error al subir el archivo')
    )
  }
}

/**
 * Procesa la imagen principal de un producto en el backend (quitar fondo, imagen de fondo,
 * marca de agua, compresión a WEBP <= 150 KB) y la sube al storage existente (Bunny).
 * Devuelve { url, key, width, height, format, size_bytes }.
 * @param {File} file Imagen original
 * @param {Object} options
 * @param {number|null} options.productId Si el producto ya existe, se guarda en products/{id}/...
 * @param {boolean} options.removeBg Elimina el fondo con rembg (servidor)
 * @param {number|null} options.backgroundId ID del asset de fondo (de /uploads/assets/)
 * @param {number|null} options.watermarkId ID del asset de marca de agua (de /uploads/assets/)
 * @param {number|null} options.watermarkX Posición del CENTRO del logo en % del ancho (0-100)
 * @param {number|null} options.watermarkY Posición del CENTRO del logo en % del alto (0-100)
 * @param {boolean} [options.previewOnly] Si true, el backend procesa y devuelve el
 *        resultado como data-URL (campo `preview`) SIN subirlo al storage. Sirve
 *        para mostrar el resultado en el modal antes de confirmar.
 * @param {string} [options.previewData] Alternativa a file: data-URL WebP devuelto
 *        por una llamada previa previewOnly=true con las mismas opciones. El
 *        backend valida y sube ese WebP SIN volver a procesar (evita la segunda
 *        inferencia al confirmar "Sí, guardar").
 */
export const processProductImage = async (file, options = {}) => {
  const formData = new FormData()
  if (file) {
    formData.append('file', file)
  }
  if (options.productId) {
    formData.append('product_id', options.productId)
  }
  if (options.removeBg) {
    formData.append('remove_bg', 'true')
  }
  if (options.backgroundId) {
    formData.append('background_id', options.backgroundId)
  }
  if (options.watermarkId) {
    formData.append('watermark_id', options.watermarkId)
  }
  if (options.watermarkX !== undefined && options.watermarkX !== null) {
    formData.append('watermark_x', options.watermarkX)
  }
  if (options.watermarkY !== undefined && options.watermarkY !== null) {
    formData.append('watermark_y', options.watermarkY)
  }
  if (options.previewOnly) {
    formData.append('preview_only', 'true')
  }
  if (options.previewData) {
    formData.append('preview_data', options.previewData)
  }

  try {
    const response = await apiClient.post('/uploads/process-image/', formData)
    return response.data
  } catch (error) {
    throw new Error(
      drfErrorMessage(error.response?.data, 'Error al procesar la imagen')
    )
  }
}

/**
 * Precarga el modelo de quitar fondo en el servidor (backend 'worker': arranca
 * el subproceso del modelo y carga el ONNX). La primera vez tarda unos segundos;
 * después las previews/guardados no pagan esa espera. Admin only.
 *
 * Si el servidor todavía no tiene el .onnx en disco responde 202 con
 * `{ ready: false, preparing: true }` y dispara la descarga en background
 * (~179 MB, una sola vez por deploy): NO bloquea. Reintentar en unos segundos.
 * @returns {Promise<{ready: boolean, preparing?: boolean, model: string, ms?: number, backend?: string, detail?: string}>}
 */
export const warmupBgModel = async () => {
  try {
    const response = await apiClient.post('/uploads/bgrm-warmup/')
    return response.data
  } catch (error) {
    throw new Error(drfErrorMessage(error.response?.data, 'Error al preparar el modelo'))
  }
}

/**
 * Lista los assets de imagen reutilizables (fondos / marcas de agua) guardados en la DB.
 * @param {'background'|'watermark'} kind
 * @returns {Promise<Array>}
 */
export const getImageAssets = async (kind) => {
  try {
    const response = await apiClient.get('/uploads/assets/', { params: { kind } })
    return Array.isArray(response.data) ? response.data : (response.data?.results || [])
  } catch (error) {
    throw new Error(drfErrorMessage(error.response?.data, 'Error al obtener los assets'))
  }
}

/**
 * Sube un fondo o marca de agua nueva; queda guardada en la DB para reutilizarla.
 * @param {File} file
 * @param {'background'|'watermark'} kind
 * @param {string} name Nombre opcional (default: nombre del archivo)
 */
export const createImageAsset = async (file, kind, name = '') => {
  const formData = new FormData()
  formData.append('file', file)
  formData.append('kind', kind)
  if (name) {
    formData.append('name', name)
  }
  try {
    const response = await apiClient.post('/uploads/assets/', formData)
    return response.data
  } catch (error) {
    throw new Error(drfErrorMessage(error.response?.data, 'Error al subir el asset'))
  }
}

/**
 * Elimina un fondo/marca de agua guardado (borra el archivo del storage y la fila).
 * @param {number} id ID del asset (de /uploads/assets/)
 */
export const deleteImageAsset = async (id) => {
  try {
    await apiClient.delete(`/uploads/assets/${id}/`)
    return true
  } catch (error) {
    throw new Error(drfErrorMessage(error.response?.data, 'Error al eliminar el asset'))
  }
}

// Función para obtener las imágenes del carousel públicas
export const getPublicCarouselImages = async () => {
  try {
    const response = await apiClient.get('/uploads/carousel/public_carousel/')
    return response.data
  } catch (error) {
    console.error('Error fetching carousel images:', error)
    throw new Error(error.response?.data?.message || 'Error al obtener las imágenes del carousel')
  }
}

// Función para obtener las imágenes del carousel (admin)
export const getCarouselImages = async () => {
  try {
    const response = await apiClient.get('/uploads/carousel/')
    return response.data
  } catch (error) {
    console.error('Error fetching carousel images:', error)
    throw new Error(error.response?.data?.message || 'Error al obtener las imágenes del carousel')
  }
}

// Función para actualizar el estado activo de una imagen
export const updateCarouselImageStatus = async (id, isActive) => {
  try {
    const response = await apiClient.patch(
      `/uploads/carousel/${id}/`,
      { is_active: isActive },
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`
        }
      }
    )
    return response.data
  } catch (error) {
    console.error('Error updating carousel image status:', error)
    throw new Error(error.response?.data?.message || 'Error al actualizar el estado de la imagen')
  }
}

// Función para cambiar el orden de las imágenes del carousel
export const updateCarouselImageOrder = async (imageId1, newOrder1, imageId2, newOrder2) => {
  try {
    // Realizamos las dos actualizaciones en paralelo
    const [response1, response2] = await Promise.all([
      apiClient.patch(
        `/uploads/carousel/${imageId1}/`, 
        { order: newOrder1 },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('accessToken')}`
          }
        }
      ),
      apiClient.patch(
        `/uploads/carousel/${imageId2}/`, 
        { order: newOrder2 },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('accessToken')}`
          }
        }
      )
    ])
    
    return { image1: response1.data, image2: response2.data }
  } catch (error) {
    console.error('Error updating carousel image order:', error)
    throw new Error(error.response?.data?.message || 'Error al actualizar el orden de las imágenes')
  }
}

// Subida al endpoint genérico + JSON al carousel (evita PATCH multipart, origen frecuente de "Not a valid string.")
const CAROUSEL_UPLOAD_SUBDIR = 'carousel'

// Función para subir una nueva imagen al carousel
export const uploadCarouselImage = async (imageData) => {
  if (!imageData.desktopImage) {
    throw new Error('Falta la imagen principal')
  }
  const desktopRes = await uploadFile(imageData.desktopImage, {
    subdirectory: CAROUSEL_UPLOAD_SUBDIR
  })
  const body = {
    desktop_image: desktopRes.url,
    link: imageData.link || '/productos',
    order: imageData.order ?? 0,
    is_active:
      imageData.isActive !== undefined && imageData.isActive !== null
        ? !!imageData.isActive
        : true
  }
  if (imageData.mobileImage) {
    const mobileRes = await uploadFile(imageData.mobileImage, {
      subdirectory: CAROUSEL_UPLOAD_SUBDIR
    })
    body.mobile_image = mobileRes.url
  }

  try {
    const response = await apiClient.post('/uploads/carousel/', body)
    return response.data
  } catch (error) {
    console.error('Error uploading carousel image:', error)
    throw new Error(
      drfErrorMessage(error.response?.data, 'Error al subir la imagen del carousel')
    )
  }
}

// Función para actualizar una imagen existente en el carousel
export const updateCarouselImage = async (id, imageData) => {
  const body = {}
  if (imageData.link) body.link = imageData.link
  if (imageData.order !== undefined) body.order = imageData.order
  if (imageData.isActive !== undefined && imageData.isActive !== null) {
    body.is_active = !!imageData.isActive
  }
  if (imageData.desktopImage) {
    const res = await uploadFile(imageData.desktopImage, {
      subdirectory: CAROUSEL_UPLOAD_SUBDIR
    })
    body.desktop_image = res.url
  }
  if (imageData.mobileImage) {
    const res = await uploadFile(imageData.mobileImage, {
      subdirectory: CAROUSEL_UPLOAD_SUBDIR
    })
    body.mobile_image = res.url
  }

  if (Object.keys(body).length === 0) {
    throw new Error('No hay cambios para guardar')
  }

  try {
    const response = await apiClient.patch(`/uploads/carousel/${id}/`, body)
    return response.data
  } catch (error) {
    console.error('Error updating carousel image:', error)
    throw new Error(
      drfErrorMessage(error.response?.data, 'Error al actualizar la imagen')
    )
  }
}

// Nueva función para eliminar completamente una imagen del carousel
export const deleteCarouselImage = async (id) => {
  try {
    const response = await apiClient.delete(
      `/uploads/carousel/${id}/`,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`
        }
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error deleting carousel image:', error);
    throw new Error(error.response?.data?.detail || 'Error al eliminar la imagen');
  }
}

